import { useState, useCallback } from 'react';
import { updateFamilyCompletion } from '../utils/familyCompletion';

/**
 * Custom hook for managing upload functionality
 * Handles upload process, progress tracking, and post-upload actions
 */
export const useUploadManagement = ({
  currentUser,
  verseAudioURLs,
  excludedVerseNumbers,
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  onUploadSuccess,
  onUploadError,
  onUploadComplete
}) => {
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(''); // 'success', 'error'
  const [uploadMessage, setUploadMessage] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);


  // Main upload function
  const handleUpload = useCallback(async () => {
    if (!verseAudioURLs || Object.keys(verseAudioURLs).length === 0) {
      setUploadStatus('error');
      setUploadMessage('No recordings to upload. Please record some verses first.');
      setShowUploadModal(true);
      return;
    }
    
    setIsUploading(true);
    try {
      // Import Firebase functions with error handling
      let ref, uploadBytes, getDownloadURL, collection, addDoc, serverTimestamp, getDoc, doc, storage, db;
      
      try {
        const firebaseStorage = await import('firebase/storage');
        const firebaseFirestore = await import('firebase/firestore');
        const firebaseConfig = await import('../config/firebase');
        
        ref = firebaseStorage.ref;
        uploadBytes = firebaseStorage.uploadBytes;
        getDownloadURL = firebaseStorage.getDownloadURL;
        collection = firebaseFirestore.collection;
        addDoc = firebaseFirestore.addDoc;
        serverTimestamp = firebaseFirestore.serverTimestamp;
        getDoc = firebaseFirestore.getDoc;
        doc = firebaseFirestore.doc;
        storage = firebaseConfig.storage;
        db = firebaseConfig.db;
      } catch (importError) {
        throw new Error('Failed to load Firebase services. Please check your internet connection.');
      }
      
      // Check if user is authenticated
      if (!currentUser) {
        throw new Error('User not authenticated. Please sign in again.');
      }
      
      // Get user's family IDs from currentUser (already includes Firestore data)
      const userFamilyIds = currentUser.familyIds || [];
      
      console.log('🎵 Starting audio splitting and upload process...');
      
      // Generate unique ID for the recording session
      const sessionId = Date.now().toString();
      
      // Create array to store all verse documents
      const verseDocuments = [];
      
      // Use the verse files that were already created (already checked above)
      console.log('🎵 Using pre-created verse files:', Object.keys(verseAudioURLs));
      
      // Process each verse file with error handling
      for (const [verseKey, verseFile] of Object.entries(verseAudioURLs)) {
        if (!verseFile) continue;
        
        try {
          const verseNumber = verseFile.verseNumber;
          // Skip verses the user deselected in the review modal
          if (excludedVerseNumbers && excludedVerseNumbers.has(verseNumber)) {
            console.log(`⏭️ Skipping deselected verse ${verseNumber}`);
            continue;
          }
          const { getBookInfo } = await import('../utils/bibleData');
          const versePassage = `${getBookInfo(selectedBook)?.name} ${selectedChapter}:${verseNumber}`;
          
          // Create verse document using the verse file data
          const verseDoc = {
            userId: currentUser.uid,
            userEmail: currentUser.email,
            name: currentUser.displayName || currentUser.firstName || currentUser.email || 'Family Member',
            book: selectedBook.toLowerCase(),
            chapter: selectedChapter,
            verse: verseNumber,
            passage: versePassage,
            audioURL: verseFile.downloadURL, // Use the verse file URL
            filename: verseFile.filename,
            sessionId: sessionId,
            startTime: verseFile.startTime || 0, // For new segmented files, startTime should be 0
            endTime: verseFile.endTime || verseFile.duration, // For new segmented files, endTime should be the full duration
            duration: verseFile.duration,
            familyIds: userFamilyIds,
            fileSizeMB: Math.round((verseFile.size / (1024 * 1024)) * 100) / 100,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          
          verseDocuments.push(verseDoc);
          console.log(`✅ Processed verse ${verseNumber}: ${verseFile.duration}s duration, ${Math.round((verseFile.size / (1024 * 1024)) * 100) / 100}MB`);
        } catch (verseError) {
          console.error(`Error processing verse ${verseKey}:`, verseError);
          throw new Error(`Failed to process verse ${verseFile?.verseNumber || 'unknown'}. Please try again.`);
        }
      }
      
      // Nothing selected to upload
      if (verseDocuments.length === 0) {
        setIsUploading(false);
        setUploadStatus('error');
        setUploadMessage('No verses selected. Please select at least one verse to upload.');
        setShowUploadModal(true);
        return;
      }

      // Save all verse documents to Firestore with error handling
      console.log(`💾 Saving ${verseDocuments.length} verse documents to Firestore...`);
      try {
        for (const verseDoc of verseDocuments) {
          await addDoc(collection(db, 'recordings'), verseDoc);
        }
      } catch (firestoreError) {
        console.error('Firestore save error:', firestoreError);
        throw new Error('Failed to save recordings to database. Please check your internet connection and try again.');
      }
      
      // Create recording session document to trigger email notification
      console.log(`📧 Creating recording session document for email notification...`);
      try {
        const sessionDoc = {
          sessionId: sessionId,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          name: currentUser.displayName || currentUser.firstName || currentUser.email || 'Family Member',
          book: selectedBook,
          chapter: selectedChapter,
          startVerse: startVerse,
          endVerse: endVerse,
          verseCount: verseDocuments.length,
          familyIds: userFamilyIds,
          createdAt: serverTimestamp()
        };
        await addDoc(collection(db, 'recordingSessions'), sessionDoc);
      } catch (sessionError) {
        console.error('Session document error:', sessionError);
        // Don't throw here - this is not critical for the main upload
        console.warn('Failed to create session document, but recordings were saved');
      }
      
      // Update family completion status
      console.log(`📊 Updating family completion status...`);
      console.log(`📊 User family IDs:`, userFamilyIds);
      console.log(`📊 Verse numbers to update:`, verseDocuments.map(doc => doc.verse));
      console.log(`📊 Book/Chapter: ${selectedBook} ${selectedChapter}`);
      
      let chapterCompleted = false;
      try {
        const verseNumbers = verseDocuments.map(doc => doc.verse);
        for (const familyId of userFamilyIds) {
          console.log(`📊 Updating family ${familyId} with verses:`, verseNumbers);
          await updateFamilyCompletion(familyId, selectedBook, selectedChapter, verseNumbers, db);
        }
        console.log(`✅ Family completion update completed successfully`);

        // Refresh family completion data after upload
        console.log('🔄 Refreshing family completion data after upload...');
        const { getFamilyCompletionData } = await import('../utils/familyCompletion');
        const refreshedFamilyCompletionData = await getFamilyCompletionData(userFamilyIds[0], db);
        console.log('🔄 Refreshed family completion data:', refreshedFamilyCompletionData);

        // If this was the last verse in the chapter, refresh the plan advancement
        const { getVersesForChapter } = await import('../utils/bibleData');
        const allVerses = getVersesForChapter(selectedBook, selectedChapter);
        const recordedVerses = new Set(verseNumbers);
        const allVersesRecorded = allVerses.every(verse => recordedVerses.has(verse));
        chapterCompleted = allVersesRecorded;

        if (allVersesRecorded) {
          console.log('🎉 Chapter completed! Refreshing plan advancement...');
          // Trigger a refresh of the plan advancement
          if (onUploadSuccess) {
            onUploadSuccess({ shouldAdvance: true });
          }
        }
      } catch (familyError) {
        console.error('❌ Family completion error:', familyError);
        // Don't throw here - this is not critical for the main upload
        console.warn('Failed to update family completion, but recordings were saved');
      }


      // Show success modal
      setUploadStatus('success');
      setUploadMessage(`Successfully uploaded ${verseDocuments.length} individual verse recordings!`);
      setShowUploadModal(true);

      // Trigger dashboard refresh by dispatching a custom event
      window.dispatchEvent(new CustomEvent('recordingsUploaded', {
        detail: {
          verseCount: verseDocuments.length,
          book: selectedBook,
          chapter: selectedChapter
        }
      }));

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess({
          verseDocuments,
          verseCount: verseDocuments.length,
          book: selectedBook,
          chapter: selectedChapter,
          chapterCompleted
        });
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Failed to upload recording. Please try again or start over.';
      
      if (error.message.includes('network') || error.message.includes('connection')) {
        errorMessage = 'Network connection lost. Please check your internet and try again.';
      } else if (error.message.includes('authenticated') || error.message.includes('permission')) {
        errorMessage = 'Authentication error. Please sign in again and try uploading.';
      } else if (error.message.includes('storage') || error.message.includes('quota')) {
        errorMessage = 'Storage error. Please try again or contact support if the problem persists.';
      } else if (error.message.includes('Firebase')) {
        errorMessage = 'Database error. Please check your connection and try again.';
      }
      
      // Show error modal
      setUploadStatus('error');
      setUploadMessage(errorMessage);
      setShowUploadModal(true);
      
      // Call error callback
      if (onUploadError) {
        onUploadError(error, errorMessage);
      }
    } finally {
      setIsUploading(false);
      
      // Call completion callback
      if (onUploadComplete) {
        onUploadComplete();
      }
    }
  }, [
    currentUser,
    verseAudioURLs,
    excludedVerseNumbers,
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    onUploadSuccess,
    onUploadError,
    onUploadComplete
  ]);

  return {
    // State
    isUploading,
    uploadStatus,
    uploadMessage,
    showUploadModal,
    
    // Setters
    setIsUploading,
    setUploadStatus,
    setUploadMessage,
    setShowUploadModal,
    
    // Functions
    handleUpload
  };
};
